// Theme tokens (ST-00127). react-select emits these as inline/emotion styles,
// so `var(--…)` resolves against <html> and follows the active theme with no
// re-render needed. Pick by ROLE, not by the hex these used to be.
const PRIMARY = 'var(--accent)';
const PRIMARY_ALPHA = (a) => `rgb(var(--accent-rgb) / ${a})`;
const PRIMARY_TEXT = 'var(--accent-text)';
const PRIMARY_SOFT = 'var(--accent-soft)';
const SURFACE = 'var(--surface)';
const SURFACE_RAISED = 'var(--surface-raised)';
const BORDER = 'var(--border)';
const BORDER_HOVER = 'var(--border-strong)';
const TEXT = 'var(--text-strong)';
const TEXT_MUTED = 'var(--text-muted)';
const TEXT_ON_ACCENT = 'var(--text-on-accent)';

export const themedSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '38px',
    backgroundColor: SURFACE,
    borderColor: state.isFocused ? PRIMARY : BORDER,
    boxShadow: state.isFocused ? `0 0 0 2px ${PRIMARY_ALPHA(0.25)}` : 'none',
    '&:hover': { borderColor: state.isFocused ? PRIMARY : BORDER_HOVER },
    fontSize: '0.875rem',
    cursor: 'pointer',
  }),
  valueContainer: (base) => ({ ...base, padding: '0 8px' }),
  input: (base) => ({ ...base, margin: 0, padding: 0, color: TEXT }),
  singleValue: (base) => ({ ...base, color: TEXT }),
  placeholder: (base) => ({ ...base, color: TEXT_MUTED }),
  indicatorSeparator: (base) => ({ ...base, backgroundColor: BORDER }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? PRIMARY : TEXT_MUTED,
    '&:hover': { color: PRIMARY },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: TEXT_MUTED,
    '&:hover': { color: PRIMARY },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 60,
    fontSize: '0.875rem',
    backgroundColor: SURFACE_RAISED,
    border: `1px solid ${BORDER}`,
    boxShadow:
      '0 4px 6px -1px rgb(var(--shadow-rgb) / var(--shadow-a-1)), 0 2px 4px -1px rgb(var(--shadow-rgb) / var(--shadow-a-2))',
  }),
  // Portalled menus escape the app container, so they must carry the surface
  // with them — otherwise they inherit the page background in dark mode.
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menuList: (base) => ({ ...base, backgroundColor: 'transparent' }),
  noOptionsMessage: (base) => ({ ...base, color: TEXT_MUTED }),
  loadingMessage: (base) => ({ ...base, color: TEXT_MUTED }),
  groupHeading: (base) => ({ ...base, color: TEXT_MUTED }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? PRIMARY : state.isFocused ? PRIMARY_SOFT : 'transparent',
    color: state.isSelected ? TEXT_ON_ACCENT : TEXT,
    cursor: 'pointer',
    ':active': {
      ...base[':active'],
      backgroundColor: state.isSelected ? PRIMARY : PRIMARY_SOFT,
    },
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: PRIMARY_SOFT,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: PRIMARY_TEXT,
    fontWeight: 500,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: PRIMARY_TEXT,
    ':hover': { backgroundColor: PRIMARY, color: TEXT_ON_ACCENT },
  }),
};

export const themedSelectTheme = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    // react-select's built-in palette — used for focus/hover states we do not
    // override explicitly. Neutrals must follow the theme, not stay light.
    primary: PRIMARY,
    primary75: PRIMARY_ALPHA(0.75),
    primary50: PRIMARY_ALPHA(0.5),
    primary25: PRIMARY_SOFT,
    neutral0: SURFACE,
    neutral5: 'var(--surface-sunken)',
    neutral10: 'var(--surface-hover)',
    neutral20: BORDER,
    neutral30: BORDER_HOVER,
    neutral40: TEXT_MUTED,
    neutral50: TEXT_MUTED,
    neutral60: TEXT,
    neutral70: TEXT,
    neutral80: TEXT,
    neutral90: TEXT,
    danger: 'var(--danger)',
    dangerLight: 'var(--danger-soft)',
  },
});
